import type { Money } from './money';

/** Минимум полей операции, нужных для расчёта (DTO или строка БД). */
export type BalanceInput = {
  payer_id: string;
  amount: number;
  currency: string;
  status: 'ACTIVE' | 'DISPUTED';
  deleted_at: string | Date | null;
};

export type Balances = { balances: Money[]; disputed: Money[] };

/**
 * Balance engine (PROJECT_SPEC §11). Для наблюдателя `me`:
 *   delta = +amount если payer = me, иначе −amount.
 * ACTIVE → balances, DISPUTED → disputed, удалённые не учитываются. Тип операции не участвует.
 * Та же логика, что в SQL BalanceRepo; тест эквивалентности — apps/api/test/balance.test.ts.
 */
export function computeBalances(txs: Iterable<BalanceInput>, me: string): Balances {
  const active = new Map<string, number>();
  const disputed = new Map<string, number>();
  for (const t of txs) {
    if (t.deleted_at) continue;
    const delta = t.payer_id === me ? t.amount : -t.amount;
    const target = t.status === 'DISPUTED' ? disputed : active;
    target.set(t.currency, (target.get(t.currency) ?? 0) + delta);
  }
  const toMoney = (m: Map<string, number>): Money[] =>
    [...m]
      .filter(([, amount]) => amount !== 0)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([currency, amount]) => ({ currency, amount }));
  return { balances: toMoney(active), disputed: toMoney(disputed) };
}

/** Применить одну операцию к уже посчитанным балансам (optimistic update на клиенте). */
export function applyTransaction(current: Balances, tx: BalanceInput, me: string): Balances {
  const asInputs: BalanceInput[] = [
    ...current.balances.map((m) => ({
      payer_id: me,
      amount: m.amount,
      currency: m.currency,
      status: 'ACTIVE' as const,
      deleted_at: null,
    })),
    ...current.disputed.map((m) => ({
      payer_id: me,
      amount: m.amount,
      currency: m.currency,
      status: 'DISPUTED' as const,
      deleted_at: null,
    })),
    tx,
  ];
  return computeBalances(asInputs, me);
}
